import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDatos } from '../contexts/DatosContext';
import type { Capacidad, Entorno, Familia, Intensidad, NivelEjercicio, Recurso } from '../types';
import { CAPACIDADES, ENTORNOS, FAMILIAS, INTENSIDADES, NIVELES_EJERCICIO } from '../data/constantes';
import { Tarjeta, TituloSeccion } from '../components/ui/Tarjeta';
import { Boton } from '../components/ui/Boton';
import { Aviso } from '../components/ui/Aviso';
import { Modal } from '../components/ui/Modal';
import { Insignia } from '../components/ui/Etiquetas';
import { CampoArea, CampoNumero, CampoSelect, CampoTexto, Casilla } from '../components/ui/Campo';
import { nuevoId } from '../utils/id';
function recursoVacio(): Recurso {
  return {
    id: nuevoId('P'),
    nombre: '',
    alias: [],
    familia: 'Activación y movilidad',
    nivelMinimo: 'Introducción',
    nivelesRecomendados: ['Introducción'],
    entornos: ['Piso'],
    intensidad: 'Baja',
    duracionMin: 3,
    duracionMax: 5,
    objetivo: '',
    capacidades: [],
    prerrequisitos: [],
    regresion: '',
    progresion: '',
    claveDocente: '',
    seguridad: '',
    bilateral: false,
    requiereEvaluacionDocente: true,
    aptoMultinivel: false,
    personalizado: true,
    dinamico: false,
    creadoEn: new Date().toISOString()
  };
}
export function EjerciciosPersonalizados() {
  const {
    personalizados,
    guardarPersonalizado,
    eliminarPersonalizado,
    recursos,
    notificar
  } = useDatos();
  const ubicacion = useLocation();
  const [formulario, setFormulario] = useState<Recurso>(recursoVacio);
  const [aliasTexto, setAliasTexto] = useState('');
  const [prerrequisitosTexto, setPrerrequisitosTexto] = useState('');
  const [confirmacionDinamico, setConfirmacionDinamico] = useState(false);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aEliminar, setAEliminar] = useState<Recurso | null>(null);
  useEffect(() => {
    const estado = ubicacion.state as {
      copiaDe?: string;
    } | null;
    if (!estado?.copiaDe) return;
    const origen = recursos.find(r => r.id === estado.copiaDe);
    if (!origen) return;
    setFormulario({
      ...origen,
      id: nuevoId('P'),
      nombre: `${origen.nombre} (copia)`,
      personalizado: true,
      dinamico: false,
      origenId: origen.id,
      creadoEn: new Date().toISOString()
    });
    setAliasTexto(origen.alias.join(', '));
    setPrerrequisitosTexto(origen.prerrequisitos.join(', '));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ubicacion.state]);
  const cambiar = (parcial: Partial<Recurso>) => setFormulario(prev => ({
    ...prev,
    ...parcial
  }));
  const alternarCapacidad = (c: Capacidad) => {
    cambiar({
      capacidades: formulario.capacidades.includes(c) ? formulario.capacidades.filter(x => x !== c) : [...formulario.capacidades, c]
    });
  };
  const alternarEntorno = (e: Entorno) => {
    cambiar({
      entornos: formulario.entornos.includes(e) ? formulario.entornos.filter(x => x !== e) : [...formulario.entornos, e]
    });
  };
  const limpiar = () => {
    setFormulario(recursoVacio());
    setAliasTexto('');
    setPrerrequisitosTexto('');
    setConfirmacionDinamico(false);
    setErrores({});
  };
  const guardar = () => {
    const nuevos: Record<string, string> = {};
    if (!formulario.nombre.trim()) nuevos.nombre = 'El nombre es obligatorio.';
    if (!formulario.objetivo.trim()) nuevos.objetivo = 'Describí el objetivo del ejercicio.';
    if (formulario.entornos.length === 0) nuevos.entornos = 'Elegí al menos un entorno.';
    if (formulario.duracionMin > formulario.duracionMax) nuevos.duracion = 'La duración mínima no puede superar a la máxima.';
    if (formulario.dinamico && !confirmacionDinamico) nuevos.dinamico = 'Necesitás marcar la confirmación profesional para guardar este contenido.';
    setErrores(nuevos);
    if (Object.keys(nuevos).length > 0) return;
    const recurso: Recurso = {
      ...formulario,
      alias: aliasTexto.split(',').map(a => a.trim()).filter(Boolean),
      prerrequisitos: prerrequisitosTexto.split(',').map(p => p.trim()).filter(Boolean),
      nivelesRecomendados: [formulario.nivelMinimo]
    };
    guardarPersonalizado(recurso);
    notificar('Ejercicio personalizado guardado.');
    limpiar();
  };
  return <div className="space-y-5" data-id="element-466" data-mp="3pqurokx5bcw5">
      <TituloSeccion nivel={1} descripcion="Los 64 recursos base no se pueden editar ni eliminar: se copian y se personalizan. Acá creás y administrás tu propio contenido." data-id="element-467" data-mp="3kumgfim2k3u9">
        Ejercicios personalizados
      </TituloSeccion>

      <Tarjeta data-id="element-468" data-mp="3uq7cz18dcbwm">
        <h2 className="text-lg font-bold text-tinta-titulo" data-id="element-469" data-mp="3uqh7n24cz78n">
          {personalizados.some(r => r.id === formulario.id) ? 'Editar ejercicio' : 'Nuevo ejercicio'}
        </h2>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-470" data-mp="323cxyjubm4qj">
          <CampoTexto etiqueta="Nombre" requerido value={formulario.nombre} error={errores.nombre} onChange={e => cambiar({
          nombre: e.target.value
        })} data-id="element-471" data-mp="1hkj33s9rqmj9" />
          <CampoTexto etiqueta="Alias" value={aliasTexto} ayuda="Separados por comas." onChange={e => setAliasTexto(e.target.value)} data-id="element-472" data-mp="18mqnen04xcvc" />
          <CampoSelect etiqueta="Familia" value={formulario.familia} opciones={FAMILIAS} onChange={e => cambiar({
          familia: e.target.value as Familia
        })} data-id="element-473" data-mp="20cv5gf0f9nte" />
          <CampoSelect etiqueta="Nivel mínimo" value={formulario.nivelMinimo} opciones={NIVELES_EJERCICIO} onChange={e => cambiar({
          nivelMinimo: e.target.value as NivelEjercicio
        })} data-id="element-474" data-mp="1rdhu1gsqmp61" />
          <CampoSelect etiqueta="Intensidad" value={formulario.intensidad} opciones={INTENSIDADES} onChange={e => cambiar({
          intensidad: e.target.value as Intensidad
        })} data-id="element-475" data-mp="kvl9iddr8uno" />
          <div className="grid grid-cols-2 gap-3" data-id="element-476" data-mp="2hlxp95xc3vl4">
            <CampoNumero etiqueta="Duración mínima" min={1} max={60} value={formulario.duracionMin} onChange={e => cambiar({
            duracionMin: Number(e.target.value)
          })} data-id="element-477" data-mp="2cufqmoisa73b" />
            <CampoNumero etiqueta="Duración máxima" min={1} max={90} value={formulario.duracionMax} error={errores.duracion} onChange={e => cambiar({
            duracionMax: Number(e.target.value)
          })} data-id="element-478" data-mp="23vkfqz4so8to" />
          </div>
        </div>

        <fieldset className="mt-4" data-id="element-479" data-mp="1pwb0c1oexiqr">
          <legend className="mb-2 text-sm font-semibold text-tinta-titulo" data-id="element-480" data-mp="3oacpb4gfc5dq">Entornos</legend>
          <div className="flex flex-wrap gap-2" data-id="element-481" data-mp="3k7ptytkjo2bs">
            {ENTORNOS.map(e => <button key={e} type="button" aria-pressed={formulario.entornos.includes(e)} onClick={() => alternarEntorno(e)} className={`min-h-[44px] rounded-xl border px-3 text-sm font-semibold ${formulario.entornos.includes(e) ? 'border-vino bg-vino/5 text-vino' : 'border-linea bg-white text-tinta-sec'}`} data-id="element-482" data-mp="160hz8zjn49pw">
                {e}
              </button>)}
          </div>
          {errores.entornos && <p className="mt-1 text-xs font-semibold text-alerta" data-id="element-483" data-mp="s2jsogbk9hky">{errores.entornos}</p>}
        </fieldset>

        <fieldset className="mt-4" data-id="element-484" data-mp="1115d6h0lf6od">
          <legend className="mb-2 text-sm font-semibold text-tinta-titulo" data-id="element-485" data-mp="1126l4cmbbd1v">Capacidades</legend>
          <div className="flex flex-wrap gap-2" data-id="element-486" data-mp="27go13758zgx2">
            {CAPACIDADES.map(c => <button key={c} type="button" aria-pressed={formulario.capacidades.includes(c)} onClick={() => alternarCapacidad(c)} className={`min-h-[44px] rounded-xl border px-3 text-sm font-semibold ${formulario.capacidades.includes(c) ? 'border-vino bg-vino/5 text-vino' : 'border-linea bg-white text-tinta-sec'}`} data-id="element-487" data-mp="2cfiv758o0rwh">
                {c}
              </button>)}
          </div>
        </fieldset>

        <div className="mt-4 space-y-4" data-id="element-488" data-mp="21vws4bjlop0i">
          <CampoArea etiqueta="Objetivo" requerido value={formulario.objetivo} error={errores.objetivo} onChange={e => cambiar({
          objetivo: e.target.value
        })} data-id="element-489" data-mp="21uvcruvjxgcz" />
          <CampoTexto etiqueta="Prerrequisitos" value={prerrequisitosTexto} ayuda="Separados por comas." onChange={e => setPrerrequisitosTexto(e.target.value)} data-id="element-490" data-mp="lh592p8cgu3v" />
          <CampoArea etiqueta="Regresión" value={formulario.regresion} onChange={e => cambiar({
          regresion: e.target.value
        })} data-id="element-491" data-mp="7b3avuzka6ih" />
          <CampoArea etiqueta="Progresión" value={formulario.progresion} onChange={e => cambiar({
          progresion: e.target.value
        })} data-id="element-492" data-mp="3tr7indz46yl8" />
          <CampoArea etiqueta="Indicaciones y clave docente" value={formulario.claveDocente} onChange={e => cambiar({
          claveDocente: e.target.value
        })} data-id="element-493" data-mp="2isa6pwzygov" />
          <CampoArea etiqueta="Seguridad" value={formulario.seguridad} onChange={e => cambiar({
          seguridad: e.target.value
        })} data-id="element-494" data-mp="3oq09pbl8jtcz" />
        </div>

        <div className="mt-4 space-y-2" data-id="element-495" data-mp="3froojp351nrx">
          <Casilla etiqueta="Es una habilidad bilateral" checked={formulario.bilateral} onChange={v => cambiar({
          bilateral: v
        })} data-id="element-496" data-mp="1918friwxffph" />
          <Casilla etiqueta="Requiere evaluación docente" checked={formulario.requiereEvaluacionDocente} onChange={v => cambiar({
          requiereEvaluacionDocente: v
        })} data-id="element-497" data-mp="1dxpcv2m3t4ep" />
          <Casilla etiqueta="Apto para multinivel" checked={formulario.aptoMultinivel} onChange={v => cambiar({
          aptoMultinivel: v
        })} data-id="element-498" data-mp="332t7d9h53rxa" />
          <Casilla etiqueta="Es contenido dinámico o caída" checked={Boolean(formulario.dinamico)} onChange={v => {
          cambiar({
            dinamico: v
          });
          if (!v) setConfirmacionDinamico(false);
        }} data-id="element-499" data-mp="371s6uek3qwhc" />
        </div>

        {formulario.dinamico && <div className="mt-3 rounded-xl border border-alerta/40 bg-alerta/5 p-3" data-id="element-500" data-mp="3qrm7li4solxp">
            <Casilla etiqueta="Conozco y puedo enseñar esta progresión; verifiqué la preparación, la salida, la asistencia y las condiciones de seguridad" checked={confirmacionDinamico} onChange={setConfirmacionDinamico} data-id="element-501" data-mp="2advq99oo0164" />
            {errores.dinamico && <p className="mt-1 text-xs font-semibold text-alerta" data-id="element-502" data-mp="22uw7n6912dnt">{errores.dinamico}</p>}
          </div>}

        <div className="mt-5 flex flex-wrap gap-2" data-id="element-503" data-mp="2gst66871ku54">
          <Boton onClick={guardar} data-id="element-504" data-mp="2bv1dvd2hj60e">Guardar ejercicio</Boton>
          <Boton variante="contorno" onClick={limpiar} data-id="element-505" data-mp="2bvkkvyqlk9zn">
            Limpiar formulario
          </Boton>
        </div>
      </Tarjeta>

      <section aria-label="Mis ejercicios" data-id="element-506" data-mp="1j6dop6em7whk">
        <h2 className="mb-3 text-lg font-bold text-tinta-titulo" data-id="element-507" data-mp="1j7ezravmsiza">
          Mis ejercicios ({personalizados.length})
        </h2>
        {personalizados.length === 0 ? <Aviso tipo="info" titulo="Todavía no creaste ejercicios propios" data-id="element-508" data-mp="1t41k7yk1p80s">
            Podés partir de una copia de cualquier recurso de la biblioteca o crear uno desde cero.
          </Aviso> : <ul className="grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-509" data-mp="10gnvgnqi3irr">
            {personalizados.map(r => <li key={r.id} data-id="element-510" data-mp="30h2jquucbp5k">
                <Tarjeta className="h-full" data-id="element-511" data-mp="30hvip9n25ad5">
                  <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-512" data-mp="35gylsojh96ih">
                    <h3 className="text-sm font-bold text-tinta-titulo" data-id="element-513" data-mp="3a7x4furxugow">{r.nombre}</h3>
                    <Insignia tono="violeta" data-id="element-514" data-mp="3aab1g966i2z3">Personalizado</Insignia>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-515" data-mp="3ebmqach8ak68">
                    <Insignia tono="vino" data-id="element-516" data-mp="2hkkuql6vm3d0">{r.nivelMinimo}</Insignia>
                    <Insignia data-id="element-517" data-mp="2mhugnqv23ww3">
                      {r.duracionMin}–{r.duracionMax} min
                    </Insignia>
                    <Insignia tono="azul" data-id="element-518" data-mp="2qiwimfgcyn1r">{r.entornos.join(' · ')}</Insignia>
                    {r.dinamico && <Insignia tono="alerta" data-id="element-519" data-mp="2vjaqnxhylwzs">Dinámico o caída</Insignia>}
                  </div>
                  <p className="mt-2 text-sm text-tinta-sec" data-id="element-520" data-mp="5ui9i7snuj2t">{r.objetivo}</p>
                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-521" data-mp="asjr0oafuac6">
                    <Boton tamano="sm" variante="contorno" onClick={() => {
                setFormulario(r);
                setAliasTexto(r.alias.join(', '));
                setPrerrequisitosTexto(r.prerrequisitos.join(', '));
                setConfirmacionDinamico(Boolean(r.dinamico));
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }} data-id="element-522" data-mp="aqyxn1zv0xfw">
                      Editar
                    </Boton>
                    <Boton tamano="sm" variante="peligro" onClick={() => setAEliminar(r)} data-id="element-523" data-mp="3tcad91yubitk">
                      Eliminar
                    </Boton>
                  </div>
                </Tarjeta>
              </li>)}
          </ul>}
      </section>

      <Modal abierto={Boolean(aEliminar)} titulo="Eliminar ejercicio personalizado" onCerrar={() => setAEliminar(null)} ancho="sm" acciones={<>
            <Boton variante="contorno" onClick={() => setAEliminar(null)} data-id="element-525" data-mp="1pnp52175oa1u">
              Cancelar
            </Boton>
            <Boton variante="peligro" onClick={() => {
        if (!aEliminar) return;
        eliminarPersonalizado(aEliminar.id);
        notificar('Ejercicio personalizado eliminado.');
        setAEliminar(null);
      }} data-id="element-526" data-mp="1unbk8g6jo0da">
              Eliminar definitivamente
            </Boton>
          </>} data-id="element-524" data-mp="1mm3uukhl5jxc">
        <p className="text-sm text-tinta" data-id="element-527" data-mp="1bnnyq684gaez">
          Vas a eliminar “{aEliminar?.nombre}”. Esta acción no se puede deshacer.
        </p>
      </Modal>
    </div>;
}